import { getColors, getPrimaryColorPair, darkenColor } from '../../charts/shared/palettes';
import { deploymentProfile } from '../../config/deploymentProfile';
import { J, JN, TITLE_SPACING, fileHeader } from './shared';

export function genLollipop(chartType, config, t, data) {
  // Sort
  const sorted = [...data];
  if (config.barOrdering === 'ascending') sorted.sort((a, b) => a.value - b.value);
  else if (config.barOrdering === 'descending') sorted.sort((a, b) => b.value - a.value);

  const isHorizontal = config.orientation !== 'vertical';
  const colors = getColors(config, 1, t);
  const baseColor = colors[0] || '#4C6EE6';
  const highlightColor = getPrimaryColorPair(config, t).highlight;
  const mutedColor = deploymentProfile.isExternal ? darkenColor(baseColor, 0.08) : baseColor;
  const dotSize = config.dotSize || 10;
  const lineThickness = config.lineThickness || 2;
  const showValues = !!config.showValues;
  const hasTitle = !!(config.title && String(config.title).trim());
  const showGrid = !!config.showGrid;
  const decimals = config.valueDecimals ?? 1;

  const maxVal = Math.max(...sorted.map(d => d.value));
  const valMax = maxVal * 1.1;

  const marginTop = hasTitle ? 45 : 20;
  const marginRight = showValues ? 60 : 30;
  const marginBottom = isHorizontal ? 50 : 70;
  const marginLeft = isHorizontal ? 110 : 50;

  const gridBlock = showGrid && isHorizontal
    ? `          <GridColumns scale={valueScale} height={innerHeight} stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} strokeDasharray="3 3" />`
    : showGrid && !isHorizontal
      ? `          <GridRows scale={valueScale} width={innerWidth} stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} strokeDasharray="3 3" />`
      : '';

  return (
    fileHeader(['@visx/scale', '@visx/group', '@visx/grid', '@visx/axis'], 'lollipop') +
    `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { GridRows, GridColumns } from '@visx/grid';
import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(sorted)};
const BASE_COLOR = ${J(baseColor)};
const HIGHLIGHT_COLOR = ${J(highlightColor)};
const MUTED_COLOR = ${J(mutedColor)};
const IS_HORIZONTAL = ${isHorizontal};
const HIGHLIGHT_TOP = ${!!config.highlightTop};
const DOT_SIZE = ${dotSize};
const LINE_THICKNESS = ${lineThickness};
const DECIMALS = ${decimals};
const THEME = ${JN(t)};
const VAL_DOMAIN = [0, ${valMax}];

export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${marginTop}, right: ${marginRight}, bottom: ${marginBottom}, left: ${marginLeft} };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const categoryScale = useMemo(() => scaleBand({
    domain: DATA.map(d => d.name),
    range: IS_HORIZONTAL ? [0, innerHeight] : [0, innerWidth],
    padding: 0.4,
  }), [innerWidth, innerHeight]);

  const valueScale = useMemo(() => scaleLinear({
    domain: VAL_DOMAIN,
    range: IS_HORIZONTAL ? [0, innerWidth] : [innerHeight, 0],
    nice: true,
  }), [innerWidth, innerHeight]);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>
      ${hasTitle ? `<div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: ${marginLeft}, fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${String(config.title).replace(/</g, '&lt;')}</div>` : ''}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
${gridBlock}
          {DATA.map((d, i) => {
            const isHighlighted = HIGHLIGHT_TOP && i === 0;
            const currentDotSize = isHighlighted ? DOT_SIZE * 2 : DOT_SIZE;
            const currentColor = HIGHLIGHT_TOP ? (isHighlighted ? HIGHLIGHT_COLOR : MUTED_COLOR) : BASE_COLOR;

            if (IS_HORIZONTAL) {
              const cy = (categoryScale(d.name) || 0) + categoryScale.bandwidth() / 2;
              const cx = valueScale(d.value);
              return (
                <g key={'lp-' + i}>
                  <line x1={valueScale(0)} y1={cy} x2={cx} y2={cy} stroke={currentColor} strokeWidth={isHighlighted ? LINE_THICKNESS + 0.5 : LINE_THICKNESS} />
                  <circle cx={cx} cy={cy} r={currentDotSize / 2} fill={currentColor} />
                  ${showValues ? `{true && <text x={cx + currentDotSize / 2 + 6} y={cy} dominantBaseline="middle" fontSize={isHighlighted ? 12 : 11} fontWeight={isHighlighted ? 'bold' : 'normal'} fill={THEME.foreground}>{d.value.toFixed(DECIMALS)}</text>}` : ''}
                </g>
              );
            } else {
              const cx = (categoryScale(d.name) || 0) + categoryScale.bandwidth() / 2;
              const cy = valueScale(d.value);
              return (
                <g key={'lp-' + i}>
                  <line x1={cx} y1={valueScale(0)} x2={cx} y2={cy} stroke={currentColor} strokeWidth={isHighlighted ? LINE_THICKNESS + 0.5 : LINE_THICKNESS} />
                  <circle cx={cx} cy={cy} r={currentDotSize / 2} fill={currentColor} />
                  ${showValues ? `{true && <text x={cx} y={cy - currentDotSize / 2 - 6} textAnchor="middle" fontSize={isHighlighted ? 12 : 11} fontWeight={isHighlighted ? 'bold' : 'normal'} fill={THEME.foreground}>{d.value.toFixed(DECIMALS)}</text>}` : ''}
                </g>
              );
            }
          })}

          {IS_HORIZONTAL ? (
            <>
              <AxisLeft scale={categoryScale} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, dx: -8, dy: categoryScale.bandwidth() / 2, textAnchor: 'end' })} hideAxisLine={false} hideTicks />
              <AxisBottom scale={valueScale} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'middle', dy: 4 })} />
            </>
          ) : (
            <>
              <AxisLeft scale={valueScale} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, dx: -8, textAnchor: 'end' })} />
              <AxisBottom scale={categoryScale} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'middle', dy: 4 })} hideAxisLine={false} hideTicks />
            </>
          )}
        </Group>
      </svg>
    </div>
  );
}
`
  );
}
